/**
 * Badge that shows which data plane the current admin page uses.
 * business = main Postgres · personal-finance = Oruon · external = no app DB
 */
import { Database, HardDrive, Cloud } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getAdminDatabaseScopeMeta,
  type AdminDatabaseScope,
} from "@/lib/adminDatabaseScope";

const STYLES: Record<
  AdminDatabaseScope,
  { wrap: string; icon: string; dot: string; Icon: typeof Database }
> = {
  business: {
    wrap: "border-sky-500/35 bg-sky-500/10 text-sky-100",
    icon: "text-sky-300",
    dot: "bg-sky-400",
    Icon: Database,
  },
  "personal-finance": {
    wrap: "border-violet-500/40 bg-violet-500/12 text-violet-100",
    icon: "text-violet-300",
    dot: "bg-violet-400",
    Icon: HardDrive,
  },
  external: {
    wrap: "border-white/15 bg-white/[0.04] text-white/60",
    icon: "text-white/45",
    dot: "bg-white/40",
    Icon: Cloud,
  },
};

export function AdminDatabaseBadge({
  scope,
  className,
  showService = true,
  size = "md",
}: {
  scope: AdminDatabaseScope;
  className?: string;
  /** Show service name (Postgres / personal-finance). */
  showService?: boolean;
  size?: "sm" | "md";
}) {
  // Guard: invalid scope (e.g. typo "negocio") must never crash the admin shell.
  const safeScope: AdminDatabaseScope =
    scope === "business" || scope === "personal-finance" || scope === "external"
      ? scope
      : "business";
  const meta = getAdminDatabaseScopeMeta(safeScope);
  const st = STYLES[safeScope] ?? STYLES.business;
  const Icon = st.Icon ?? Database;
  return (
    <span
      role="status"
      title={meta.title}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-full border font-medium tracking-wide",
        st.wrap,
        size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]",
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", st.dot)} aria-hidden />
      <Icon className={cn("shrink-0", st.icon, size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5")} aria-hidden />
      <span className="truncate font-semibold uppercase">{meta.label}</span>
      {showService && (
        <span className="hidden truncate font-normal opacity-70 sm:inline">
          · {meta.service}
        </span>
      )}
    </span>
  );
}
